import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import { makeStyles } from 'tss-react/mui';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { DateRange, DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import moment from 'moment';
import { getExtensionDuration } from 'app/helpers/getExtensionDuration';
import { customAddWeek } from '@AMIEWEB/Order/OrderDetails/helper';
import { setPstTimeOffset } from '@AMIEWEB/AmWorkDesk/CoverageDesk/CoverageModal/assignHelper';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
    '& .MuiInputAdornment-positionEnd': {
      color: `${theme.palette.system.red}`,
    },
  },
  checkbox: {
    '& label': {
      '& .MuiButtonBase-root.MuiCheckbox-root.Mui-checked': {
        color: `${theme.palette.system.main} !important`,
      },
      '&:hover .MuiButtonBase-root.MuiCheckbox-root': {
        color: `${theme.palette.system.main} !important`,
      },
    },
  },
}));

const PlacementTerms = () => {
  const { t } = useTranslation();
  const { control, formState: { errors }, setError, clearErrors, watch, setValue } = useFormContext();
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const isEditOrder = pathname.includes('/edit');
  const watchPlacementLengthValue = watch('placementLength');
  const watchStartDateValue = watch('startDate');
  const watchEndDateValue = watch('endDate');
  const [initialized, setInitialized] = useState<boolean>(false);
  const selectOrderDetailsUIViewData = useSelector(selectOrderDetailsUIView);
  const existingOrderEndDate = selectOrderDetailsUIViewData?.orderDetails?.endDate;

  useEffect(() => {
    if (!initialized && isEditOrder && existingOrderEndDate && watchStartDateValue && watchPlacementLengthValue) {
      setValue('endDate', setPstTimeOffset(moment(existingOrderEndDate)));
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditOrder, existingOrderEndDate, initialized, watchStartDateValue, watchPlacementLengthValue]);

  const isValidDate = value => {
    const date = moment(value);
    const year = date?.year();
    const currentYear = moment().year();
    return !(!date.isValid() || year < currentYear - 10 || year > currentYear + 10);
  };

  const validateEndDate = endDate => {
    const currentYear = moment().year();
    if (endDate && (endDate.year() < currentYear - 10 || endDate.year() > currentYear + 10)) {
      setError('endDate', { message: t('invalidDate') });
      return false;
    }
    clearErrors('endDate');
    return true;
  };

  const handleStartDateChange = (date: DateRange | null) => {
    if (date && date?.startDate && isValidDate(date.startDate)) {
      const newStartDate = setPstTimeOffset(moment(date.startDate),'YYYY-MM-DD');
      setValue('startDate', newStartDate);
      clearErrors('startDate');

      // If placementLength exists, update endDate based on placementLength
      if (watchPlacementLengthValue > 0) {
        const newEndDate = customAddWeek(date.startDate, watchPlacementLengthValue);
        if (validateEndDate(moment(newEndDate))) {
          setValue('endDate', setPstTimeOffset(moment(newEndDate),'YYYY-MM-DD'));
          clearErrors('endDate');
        } else {
          setValue('endDate', null);
          setError('endDate', { message: t('invalidDate') });
        }
      } 
      // If endDate exists but no placementLength, check if startDate is after endDate first
      else if (watchEndDateValue) {
        // If startDate is after endDate, set endDate to startDate and placementLength to 0
        if (moment(newStartDate).isAfter(moment(watchEndDateValue), 'day')) {
          setValue('endDate', newStartDate);
          setValue('placementLength', 0);
        } else {
          // Otherwise, recalculate placementLength from dates
          const calculatedLength = getExtensionDuration(moment(newStartDate), new Date(watchEndDateValue));
          setValue('placementLength', calculatedLength);
        }
      }
    } else {
      setError('startDate', { message: t('order.requiredMessage') });
    }
  };

  const handleEndDateChange = (date: DateRange | null) => {
    if (date && date?.startDate && isValidDate(date.startDate)) {
      const newEndDate = setPstTimeOffset(moment(date.startDate),'YYYY-MM-DD');
      
      // If endDate is before startDate, set endDate to startDate
      if (watchStartDateValue && moment(newEndDate).isBefore(moment(watchStartDateValue), 'day')) {
        setValue('endDate', watchStartDateValue);
        setValue('placementLength', 0);
      } else {
        setValue('endDate', newEndDate);
        
        // Recalculate placementLength from startDate and endDate
        if (watchStartDateValue) {
          const calculatedLength = getExtensionDuration(moment(watchStartDateValue), new Date(newEndDate));
          setValue('placementLength', calculatedLength);
        }
      }
      clearErrors('endDate');
    } else {
      setError('endDate', { message: t('invalidDate') });
    }
  };

  const validateControl = value => {
    return value >= 0 || value === null;
  };

  const handlePlacementLength = event => {
    const value = event.target.value ? parseInt(event.target.value) : null;
    if (value === null || validateControl(value)) {
      setValue('placementLength', value);

      if (value === null) {
        setValue('endDate', null);
      } else if (watchStartDateValue && value > 0) {
        const newEndDate = customAddWeek(watchStartDateValue, value);
        if (validateEndDate(moment(newEndDate))) {
          setValue('endDate', setPstTimeOffset(moment(newEndDate),'YYYY-MM-DD'));
          clearErrors('endDate');
        } else {
          setValue('endDate', null);
          setError('endDate', { message: t('invalidDate') });
        }
      } else if (watchStartDateValue && value === 0) {
        setValue('endDate', watchStartDateValue);
      }
    }
  };

  return (
    <Grid container columnSpacing={2} marginTop={0}>
      <Grid item xs={4}>
        <Controller
          name={'placementLength'}
          control={control}
          render={({ field }) => (
            <SingleLineInput
              id={'placementLength'}
              inputDefaultValue={field.value}
              inputLabel={t('order.newOrderCreation.orderDetails.placementTerms.placementLength')}
              inputName={'placementLength'}
              inputValue={field.value}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handlePlacementLength(event);
              }}
              showEndAdornment={true}
              endAdornmentValue={'weeks'}
              inputType={'number'}
              fieldStyle={classes.fieldStyle}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}></Grid>
      <Grid item xs={4}>
        <Grid item className={classes.checkbox} sx={{ paddingTop: '16px', paddingLeft: '12px' }}>
          <Controller
            name={'ASAP'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                defaultValue={false}
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('order.newOrderCreation.orderDetails.placementTerms.asap')}
                checkboxName={'ASAP'}
                id={'ASAP'}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={4} mt={2}>
        <Controller
          control={control}
          name={'startDate'}
          render={({ field }) => {
            const date = moment(watchStartDateValue ?? null);
            return (
              <React.Fragment>
                <DateRangePicker
                  onChange={handleStartDateChange}
                  value={watchStartDateValue}
                  onCancel={() => {}}
                  clear
                  open={false}
                  variant="filled"
                  single
                  placeholder={t('order.newOrder.startDate')}
                  labelClass={classes.fieldStyle}
                  className={classes.fieldStyle}
                  quickSelect={false}
                  useMaxWidth={true}
                  maxWidth={300}
                  textFieldSize="medium"
                  showIcon={true}
                  actionBar={false}
                  clickAway={true}
                  trailingIconShow={true}
                  readOnly={false}
                  keepClickAwayVal={true}
                  initialDateRange={{
                    startDate: date.isValid() ? new Date(date.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                    endDate: date.isValid() ? new Date(date.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                    useLabelAsValue: true,
                  }}
                  error={!isNullOrUndefined(errors?.startDate)}
                  helperText={!isNullOrUndefined(errors?.startDate) ? (errors?.startDate?.message as string) || '' : undefined}
                />
              </React.Fragment>
            );
          }}
        />
      </Grid>
      <Grid item xs={4} mt={2}>
        <Controller
          control={control}
          name={'endDate'}
          render={({ field }) => {
            const endDate = moment(watchEndDateValue ?? null);
            return (
              <React.Fragment>
                <DateRangePicker
                  onChange={handleEndDateChange}
                  value={watchEndDateValue}
                  onCancel={() => {}}
                  clear
                  open={false}
                  variant="filled"
                  single
                  placeholder={t('order.newOrder.endDate')}
                  labelClass={classes.fieldStyle}
                  className={classes.fieldStyle}
                  quickSelect={false}
                  useMaxWidth={true}
                  maxWidth={300}
                  textFieldSize="medium"
                  showIcon={true}
                  actionBar={false}
                  clickAway={true}
                  trailingIconShow={true}
                  readOnly={false}
                  keepClickAwayVal={true}
                  initialDateRange={{
                    startDate: endDate.isValid() ? new Date(endDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                    endDate: endDate.isValid() ? new Date(endDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                    useLabelAsValue: true,
                  }}
                  error={!isNullOrUndefined(errors?.endDate)}
                  helperText={!isNullOrUndefined(errors?.endDate) ? (errors?.endDate?.message as string) || '' : undefined}
                />
              </React.Fragment>
            );
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <Grid item className={classes.checkbox} sx={{ paddingTop: '16px', paddingLeft: '12px' }}>
          <Controller
            name={'flexibleDates'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                defaultValue={false}
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('order.newOrderCreation.orderDetails.placementTerms.flexibleDates')}
                checkboxName={'flexibleDates'}
                id={'flexibleDates'}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PlacementTerms;
